import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useMedia } from 'react-use';
import PhoneInput, {
  isValidPhoneNumber,
} from 'react-phone-number-input/mobile';
import 'react-phone-number-input/style.css';
import { Modal, DividerWithLogo, Typography, Icon, Button } from 'ck-ui';
import { usePhoneInputCountries } from './hooks';
import { klaviyoSignUp } from './utils';
import styles from './PopupSignup.module.scss';
import { TermsAndPrivacyLinks } from '../TermsAndPrivacyLinks';

const PinCodePopup = ({
  pinCode,
  heroImage,
  contentImage,
  popupHeading,
  successHeading,
  postSignUpHeading,
  urlContains,
}) => {
  const [show, setShow] = useState(false);
  const [hasSignedUp, setHasSignedUp] = useState(false);
  const [hasUnlocked, setHasUnlocked] = useState(false);
  const [pins, setPins] = useState([]);
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const isMobile = useMedia('(max-width: 767px)');
  const { defaultCountry, countries } = usePhoneInputCountries();

  useEffect(() => {
    // if unlocked, do not show the popup again
    if (localStorage.getItem(`pinCodeUnlocked-${pinCode}`) === 'true') return;

    if (urlContains && window.location.href.toLowerCase().includes(urlContains.toLowerCase())) {
      setHasUnlocked(true);
      localStorage.setItem(`pinCodeUnlocked-${pinCode}`, 'true');
    }
    setShow(true);
  }, [pinCode, urlContains]);

  useEffect(() => {
    const domSiteNav = window.document.querySelector('#SiteNavContainer');
    if (domSiteNav) {
      domSiteNav.classList.toggle('force-to-show', show);
    }
  }, [show]);

  const validatePhone = () => {
    const isValid = phone ? isValidPhoneNumber(phone) : false;
    setPhoneError(isValid ? null : 'Please enter a valid mobile number.');
    return isValid;
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (!validatePhone()) return;
    setIsFetching(true);
    const params = {
      phone,
      source: 'pin_code_popup',
      noEmail: true,
      onSuccess: () => setHasSignedUp(true),
      onFinal: () => setIsFetching(false)
    };
    klaviyoSignUp(params);
  };

  const handlePinChange = e => {
    const { maxLength, value, form } = e.target;
    if (value.length >= maxLength) {
      const index = [...form].indexOf(e.target);
      form.elements[index + 1]?.focus();
      const newPins = [...pins];
      newPins[index] = value;
      setPins(newPins);
      if (newPins.join('').toLowerCase() === pinCode.toLowerCase()) {
        setHasUnlocked(true);
        localStorage.setItem(`pinCodeUnlocked-${pinCode}`, 'true');
      }
      e.preventDefault();
    }
  };

  return (
    <Modal show={show} isFixedSize>
      <div className={classnames('is-flex', 'is-full-width', 'is-full-height')}>
        {!isMobile && (
          <div
            className={classnames('is-half-width', styles.image)}
            style={{ backgroundImage: `url('${heroImage}')` }}
          />
        )}

        <div className={classnames(styles.textWrapper, 'is-half-width')}>
          <DividerWithLogo />
          {hasUnlocked ? (
            <>
              <Typography variant="heading5" align="centered" uppercase>{successHeading}</Typography>
              {contentImage && (
                <div className="is-flex-centered mt-3" style={{ height: '120px' }}>
                  <img src={contentImage} alt="promotion banner" style={{ height: '100%' }} />
                </div>
              )}
              <Typography
                className={classnames(
                  'is-flex',
                  'is-flex-centered',
                  styles.largeMarginTop,
                  styles.largeMarginBottom,
                  styles.tick
                )}
              >
                <Icon color="secondary" name="check" />
              </Typography>
              <div className="has-text-centered">
                <Button onClick={() => setShow(false)} fullwidth="all" size="large" className={styles.signUpBtn}>Shop Now</Button>
              </div>
            </>
          ) : (
            <>
              <Typography variant="heading5" align="centered" uppercase>{hasSignedUp ? postSignUpHeading : popupHeading}</Typography>
              {contentImage && (
                <div className="is-flex-centered mt-3" style={{ height: '120px' }}>
                  <img src={contentImage} alt="promotion banner" style={{ height: '100%' }} />
                </div>
              )}
              <form className={styles.mediumMarginTop} onSubmit={handleSubmit}>
                <div className="has-text-centered mt-3">
                  <Typography className="mb-2" uppercase renderAs="h5" variant="heading7" color="black-bis">Enter Code For Early Access:</Typography>
                  <input
                    aria-label="Pin Code First Input"
                    className="snap-pin-digit"
                    type="text"
                    size="1"
                    maxLength="1"
                    placeholder="*"
                    onChange={handlePinChange}
                  />
                  <input
                    aria-label="Pin Code Second Input"
                    className="snap-pin-digit"
                    type="text"
                    size="1"
                    maxLength="1"
                    placeholder="*"
                    onChange={handlePinChange}
                  />
                  <input
                    aria-label="Pin Code Third Input"
                    className="snap-pin-digit"
                    type="text"
                    size="1"
                    maxLength="1"
                    placeholder="*"
                    onChange={handlePinChange}
                  />
                </div>
                {!hasSignedUp && (
                  <>
                    <label className="mt-2 has-text-centered" htmlFor="phone">
                      <Typography color="black-bis" weight={400}>Or enter your mobile phone below to get your pin*</Typography>
                    </label>
                    <PhoneInput
                      id="phone"
                      defaultCountry={defaultCountry}
                      countries={countries}
                      countryCallingCodeEditable={false}
                      countrySelectProps={{ unicodeFlags: true }}
                      value={phone}
                      className={classnames(styles.phoneInput, 'is-full-width', { [styles.error]: phoneError })}
                      onChange={setPhone}
                      onBlur={validatePhone}
                    />
                    {phoneError && (
                      <Typography className="mt-1" variant="body-small" display="block" color="danger">{phoneError}</Typography>
                    )}
                    <div className="has-text-centered">
                      <Button fullwidth="all" loading={isFetching} type="submit" size="large" className={styles.signUpBtn}>Get Pin Now</Button>
                    </div>
                  </>
                )}
              </form>
            </>
          )}
          <TermsAndPrivacyLinks />
        </div>
      </div>
    </Modal>
  );
};

PinCodePopup.propTypes = {
  pinCode: PropTypes.string.isRequired,
  heroImage: PropTypes.string.isRequired,
  contentImage: PropTypes.string,
  popupHeading: PropTypes.string.isRequired,
  successHeading: PropTypes.string.isRequired,
  postSignUpHeading: PropTypes.string.isRequired,
  urlContains: PropTypes.string
};

PinCodePopup.defaultProps = {
  contentImage: null,
  urlContains: null
};

export { PinCodePopup };
